import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowLeft, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const JEWELLERY_TYPES = [
  "Necklace", "Ring", "Earring", "Bangle", "Bracelet", "Pendant",
  "Chain", "Mangalsutra", "Nose Pin", "Anklet", "Kadli", "Other",
];

const METALS = ["Gold", "Silver"];
const PURITIES = {
  Gold: ["9 KT", "14 KT", "18 KT", "22 KT"],
  Silver: ["925", "Silver Jewellery"],
};

export default function OrderCreate() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [karigars, setKarigars] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    customer_id: params.get("customer_id") || "",
    title: "",
    description: "",
    modification_instructions: "",
    source_phone: "",
    order_date: "",
    expected_delivery: "",
    priority: "Medium",
    image_paths: [],
    jewellery_type: "",
    metal_type: "",
    purity: "",
    size: "",
    size_dimensions: "",
    finish_type: "",
    karigar_notes: "",
    sample_given: false,
    sample_tag_number: "",
    sample_weight: "",
    employee_ids: [],
    delivery_person_id: "",
    karigar_id: "",
    gold_rate_fixed: false,
    fixed_gold_rate: "",
    advance_given: false,
    advance_amount: "",
    advance_metal_type: "",
    advance_metal_weight: "",
    advance_purity_percent: "100",
    booking_type: "none",
    booked_rate: "",
    booked_weight: "",
    book_rate_only_for_paid_amount: false,
    estimated_weight: "",
    final_weight: 0,
  });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/employees?active=true").then((r) => setEmployees(r.data));
    api.get("/karigars").then((r) => setKarigars(r.data));
  }, []);

  useEffect(() => {
    if (form.customer_id) {
      const c = customers.find((c) => c.id === form.customer_id);
      if (c && !form.source_phone) {
        setForm((f) => ({ ...f, source_phone: c.primary_phone }));
      }
    }
  }, [form.customer_id, customers]);

  // Reset purity if metal_type changes
  useEffect(() => {
    setForm((f) => ({ ...f, purity: "" }));
  }, [form.metal_type]);

  const onUpload = async (files) => {
    setUploading(true);
    const newPaths = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        newPaths.push(data.path);
      } catch {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setForm((f) => ({ ...f, image_paths: [...f.image_paths, ...newPaths] }));
    setUploading(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_id) { toast.error("Select a customer"); return; }
    
    // Validations
    if (form.sample_given && (!form.sample_tag_number.trim() || !form.sample_weight)) {
      toast.error("Sample Tag Number and Sample Weight are required when Sample Given is toggled.");
      return;
    }
    if (form.gold_rate_fixed && !form.fixed_gold_rate) {
      toast.error("Please enter the fixed gold rate.");
      return;
    }
    if (form.advance_given && !form.advance_amount && (!form.advance_metal_type || form.advance_metal_type === "none" || !form.advance_metal_weight)) {
      toast.error("Please enter the advance amount or advance metal weight.");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        order_date: form.order_date ? new Date(form.order_date).toISOString() : null,
        expected_delivery: form.expected_delivery ? new Date(form.expected_delivery).toISOString() : null,
        estimated_weight: parseFloat(form.estimated_weight) || 0,
        sample_weight: parseFloat(form.sample_weight) || 0,
        fixed_gold_rate: parseFloat(form.fixed_gold_rate) || 0,
        advance_amount: parseFloat(form.advance_amount) || 0,
        advance_metal_type: form.advance_metal_type || "",
        advance_metal_weight: parseFloat(form.advance_metal_weight) || 0,
        advance_purity_percent: parseFloat(form.advance_purity_percent) || 100,
        booked_rate: parseFloat(form.booked_rate) || 0,
        booked_weight: parseFloat(form.booked_weight) || 0,
        final_weight: parseFloat(form.final_weight) || 0,
        delivery_person_id: form.delivery_person_id || null,
        karigar_id: form.karigar_id || null,
      };
      const { data } = await api.post("/orders", payload);
      toast.success(`Order ${data.order_no} created`);
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl pb-12">
      <Link to="/orders" className="text-sm text-zinc-500 hover:text-zinc-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-3.5 h-3.5" /> Orders
      </Link>
      <div>
        <div className="overline text-zinc-500">Create</div>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">New Custom Order</h1>
      </div>

      <form onSubmit={onSubmit} className="space-y-6" data-testid="order-create-form">
        {/* Reference Images */}
        <Card className="p-6 space-y-5">
          <div>
            <h3 className="font-heading font-bold text-xl">Reference Design</h3>
            <p className="text-xs text-zinc-500 mt-1">Upload one or more images. These are shared on the customer & karigar copies.</p>
          </div>
          <div>
            <input
              id="img-input"
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onUpload(Array.from(e.target.files || []))}
            />
            <label
              htmlFor="img-input"
              className="border-2 border-dashed border-border rounded-md p-8 grid place-items-center text-center cursor-pointer hover:border-zinc-900 transition-colors"
              data-testid="image-upload-area"
            >
              {uploading ? (
                <div className="flex items-center text-zinc-500"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</div>
              ) : (
                <div className="text-sm text-zinc-600">
                  <Upload className="w-5 h-5 mx-auto mb-2 text-zinc-400" />
                  <span className="font-medium">Click to upload reference designs</span>
                  <div className="text-xs text-zinc-500 mt-1">JPG, PNG, WebP — multiple allowed</div>
                </div>
              )}
            </label>
            {form.image_paths.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-4" data-testid="uploaded-images">
                {form.image_paths.map((p, i) => (
                  <div key={p} className="relative aspect-square rounded-md overflow-hidden border border-border bg-zinc-100">
                    <img src={fileUrl(p)} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-zinc-900/80 text-white grid place-items-center hover:bg-red-600"
                      onClick={() => setForm((f) => ({ ...f, image_paths: f.image_paths.filter((_, j) => j !== i) }))}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Order Details */}
        <Card className="p-6 space-y-5">
          <h3 className="font-heading font-bold text-xl">Order Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label>Order Title *</Label>
              <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Antique gold haar — modified" data-testid="order-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Customer *</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="order-customer-select"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name} {c.shop_name ? `· ${c.shop_name}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Order Source Phone *</Label>
              <Input required value={form.source_phone} onChange={(e) => setForm({ ...form, source_phone: e.target.value })} placeholder="Number used for this order" data-testid="order-source-phone" />
            </div>
            <div className="space-y-2">
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="order-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Low">Low</SelectItem>
                  <SelectItem value="Medium">Medium</SelectItem>
                  <SelectItem value="High">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Order Date</Label>
              <Input type="datetime-local" value={form.order_date} onChange={(e) => setForm({ ...form, order_date: e.target.value })} data-testid="order-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Expected Delivery Date</Label>
              <Input type="datetime-local" value={form.expected_delivery} onChange={(e) => setForm({ ...form, expected_delivery: e.target.value })} data-testid="order-delivery-input" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Customer brief in plain words…" />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Modification Instructions</Label>
              <Textarea rows={3} value={form.modification_instructions} onChange={(e) => setForm({ ...form, modification_instructions: e.target.value })} placeholder="e.g. lighter weight, smaller stones, change clasp…" />
            </div>
          </div>
        </Card>

        {/* Crafting Specifications */}
        <Card className="p-6 space-y-5 bg-amber-50/20 border-amber-200">
          <div>
            <h3 className="font-heading font-bold text-xl text-amber-950">Crafting Instructions</h3>
            <p className="text-xs text-zinc-600 mt-1">Sent to karigar only. Will appear on the Karigar Copy of the Job Sheet.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
            <div className="space-y-2">
              <Label>Jewellery Type</Label>
              <Select value={form.jewellery_type} onValueChange={(v) => setForm({ ...form, jewellery_type: v })}>
                <SelectTrigger data-testid="order-jewellery-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>
                  {JEWELLERY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Metal</Label>
                <Select value={form.metal_type} onValueChange={(v) => setForm({ ...form, metal_type: v })}>
                  <SelectTrigger data-testid="order-metal-select"><SelectValue placeholder="Metal" /></SelectTrigger>
                  <SelectContent>
                    {METALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Purity</Label>
                <Select disabled={!form.metal_type} value={form.purity} onValueChange={(v) => setForm({ ...form, purity: v })}>
                  <SelectTrigger data-testid="order-purity-select"><SelectValue placeholder="Purity" /></SelectTrigger>
                  <SelectContent>
                    {form.metal_type && PURITIES[form.metal_type].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Approx Weight Guidance (g)</Label>
              <Input
                type="number" step="0.001"
                value={form.estimated_weight}
                onChange={(e) => setForm({ ...form, estimated_weight: e.target.value })}
                placeholder="0.000"
                data-testid="order-approx-weight"
              />
            </div>
            <div className="space-y-2">
              <Label>Finish Type</Label>
              <Input value={form.finish_type} onChange={(e) => setForm({ ...form, finish_type: e.target.value })} placeholder="e.g. matte, polish, antique" data-testid="order-finish-type" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Size (e.g. Ring size)</Label>
                <Input value={form.size} onChange={(e) => setForm({ ...form, size: e.target.value })} placeholder="e.g. 14" data-testid="order-size-input" />
              </div>
              <div className="space-y-2">
                <Label>Dimensions / Length</Label>
                <Input value={form.size_dimensions} onChange={(e) => setForm({ ...form, size_dimensions: e.target.value })} placeholder="e.g. 18 inch length" data-testid="order-size-dim" />
              </div>
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Karigar Notes / Special Instructions</Label>
              <Textarea
                rows={3}
                value={form.karigar_notes}
                onChange={(e) => setForm({ ...form, karigar_notes: e.target.value })}
                placeholder="Internal notes for karigar — not shown to customer"
                data-testid="order-karigar-notes"
              />
            </div>

            {/* Sample Given toggle & fields */}
            <div className="md:col-span-2 border-t border-amber-200/50 pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold">Is Sample Given?</Label>
                  <p className="text-xs text-zinc-500">Toggle if the customer provided a physical sample piece.</p>
                </div>
                <Switch
                  checked={form.sample_given}
                  onCheckedChange={(checked) => setForm({ ...form, sample_given: checked })}
                  data-testid="sample-given-switch"
                />
              </div>

              {form.sample_given && (
                <div className="grid grid-cols-2 gap-4 p-3 bg-amber-50/50 rounded-lg border border-amber-100 animate-in fade-in slide-in-from-top-1">
                  <div className="space-y-2">
                    <Label>Sample Tag Number *</Label>
                    <Input
                      required
                      value={form.sample_tag_number}
                      onChange={(e) => setForm({ ...form, sample_tag_number: e.target.value })}
                      placeholder="e.g. TAG-983"
                      data-testid="sample-tag-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sample Weight (g) *</Label>
                    <Input
                      required
                      type="number" step="0.001"
                      value={form.sample_weight}
                      onChange={(e) => setForm({ ...form, sample_weight: e.target.value })}
                      placeholder="0.000"
                      data-testid="sample-weight-input"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Staff Assignment & Delivery */}
        <Card className="p-6 space-y-5">
          <div>
            <h3 className="font-heading font-bold text-xl">Staff & Scheduling</h3>
            <p className="text-xs text-zinc-500 mt-1">Assign internal staff members to keep track of sales and dispatch operations.</p>
          </div>
          <div className="space-y-4">
            <div className="space-y-2.5">
              <Label className="font-semibold">Assigned Employees</Label>
              {employees.length === 0 ? (
                <p className="text-xs text-zinc-400">No active employees available. Manage them in Employees Master.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="employee-checkboxes-grid">
                  {employees.map((emp) => (
                    <label key={emp.id} className="flex items-center gap-2.5 text-sm cursor-pointer p-2.5 rounded-md border border-zinc-200 hover:bg-zinc-50 transition-colors">
                      <Checkbox
                        checked={form.employee_ids.includes(emp.id)}
                        onCheckedChange={(checked) => {
                          setForm((f) => ({
                            ...f,
                            employee_ids: checked
                              ? [...f.employee_ids, emp.id]
                              : f.employee_ids.filter((id) => id !== emp.id),
                          }));
                        }}
                      />
                      <span>{emp.name} <span className="text-xs text-zinc-400">({emp.role})</span></span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
              <div className="space-y-2">
                <Label>Delivery Person</Label>
                <Select value={form.delivery_person_id} onValueChange={(v) => setForm({ ...form, delivery_person_id: v })}>
                  <SelectTrigger data-testid="order-delivery-person-select">
                    <SelectValue placeholder="Assign delivery person" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned / Self</SelectItem>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name} ({emp.role})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Assigned Karigar (Artisan)</Label>
                <Select value={form.karigar_id} onValueChange={(v) => setForm({ ...form, karigar_id: v })}>
                  <SelectTrigger data-testid="order-karigar-select">
                    <SelectValue placeholder="Assign karigar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {karigars.map((k) => (
                      <SelectItem key={k.id} value={k.id}>{k.name} ({k.craft_specialty || "General"})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </Card>

        {/* Pricing, Locking & Advance Booking */}
        <Card className="p-6 space-y-5 bg-zinc-50/50 border-zinc-200">
          <div>
            <h3 className="font-heading font-bold text-xl">Rate Booking & Advance Payment</h3>
            <p className="text-xs text-zinc-500 mt-1">Configure metal rate locks and record upfront booking deposits in customer ledger.</p>
          </div>

          <div className="space-y-5">
            {/* Gold Rate Fixed Toggle */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold">Fix Gold Rate?</Label>
                  <p className="text-xs text-zinc-500">Locks the gold/silver rate. Business rule: Requires 100% advance deposit coverage.</p>
                </div>
                <Switch
                  checked={form.gold_rate_fixed}
                  onCheckedChange={(checked) => setForm({ ...form, gold_rate_fixed: checked })}
                  data-testid="gold-rate-fixed-switch"
                />
              </div>

              {form.gold_rate_fixed && (
                <div className="max-w-xs p-3 bg-zinc-100/50 rounded border border-zinc-200 animate-in fade-in duration-200">
                  <Label>Fixed Gold Rate (₹/g) *</Label>
                  <Input
                    required
                    type="number"
                    value={form.fixed_gold_rate}
                    onChange={(e) => setForm({ ...form, fixed_gold_rate: e.target.value })}
                    placeholder="Rate per gram"
                    data-testid="fixed-gold-rate-input"
                  />
                </div>
              )}
            </div>

            {/* Advance Given Toggle */}
            <div className="border-t border-zinc-200/60 pt-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold">Advance Payment Received?</Label>
                  <p className="text-xs text-zinc-500">Toggle to enter advance booking and generate a customer ledger transaction.</p>
                </div>
                <Switch
                  checked={form.advance_given}
                  onCheckedChange={(checked) => setForm({ ...form, advance_given: checked })}
                  data-testid="advance-given-switch"
                />
              </div>

              {form.advance_given && (
                <div className="space-y-4 p-4 bg-zinc-100/50 rounded-lg border border-zinc-200 animate-in fade-in duration-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>Advance Amount (₹)</Label>
                      <Input
                        type="number"
                        value={form.advance_amount}
                        onChange={(e) => setForm({ ...form, advance_amount: e.target.value })}
                        placeholder="Amount in ₹"
                        data-testid="advance-amount-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Advance Metal</Label>
                      <Select value={form.advance_metal_type} onValueChange={(v) => setForm({ ...form, advance_metal_type: v })}>
                        <SelectTrigger data-testid="advance-metal-type"><SelectValue placeholder="Select metal" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="Gold">Gold</SelectItem>
                          <SelectItem value="Silver">Silver</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Metal Weight (g)</Label>
                      <Input
                        type="number" step="0.001"
                        value={form.advance_metal_weight}
                        onChange={(e) => setForm({ ...form, advance_metal_weight: e.target.value })}
                        placeholder="0.000"
                        data-testid="advance-metal-weight"
                        disabled={!form.advance_metal_type || form.advance_metal_type === "none"}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Purity Percentage (%)</Label>
                      <Input
                        type="number" step="0.1"
                        value={form.advance_purity_percent}
                        onChange={(e) => setForm({ ...form, advance_purity_percent: e.target.value })}
                        placeholder="100.0"
                        data-testid="advance-purity-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Advance Booking Type</Label>
                      <Select value={form.booking_type} onValueChange={(v) => setForm({ ...form, booking_type: v })}>
                        <SelectTrigger data-testid="booking-type-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Plain Payment (No Lock)</SelectItem>
                          <SelectItem value="rate">Rate Booking (Locks rate only for value paid)</SelectItem>
                          <SelectItem value="weight">Weight Booking (Locks rate for selected weight)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {form.booking_type !== "none" && (
                    <div className="grid grid-cols-2 gap-4 border-t border-zinc-200/80 pt-3 animate-in fade-in duration-150">
                      <div className="space-y-2">
                        <Label>Booked Rate (₹/g) *</Label>
                        <Input
                          required
                          type="number"
                          value={form.booked_rate}
                          onChange={(e) => setForm({ ...form, booked_rate: e.target.value })}
                          placeholder="Booking metal rate"
                          data-testid="booked-rate-input"
                        />
                      </div>
                      
                      {form.booking_type === "weight" ? (
                        <div className="space-y-2">
                          <Label>Booked Weight (g) *</Label>
                          <Input
                            required
                            type="number" step="0.001"
                            value={form.booked_weight}
                            onChange={(e) => setForm({ ...form, booked_weight: e.target.value })}
                            placeholder="Locked weight"
                            data-testid="booked-weight-input"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 pt-8">
                          <Checkbox
                            id="rate-limit"
                            checked={form.book_rate_only_for_paid_amount}
                            onCheckedChange={(checked) => setForm({ ...form, book_rate_only_for_paid_amount: !!checked })}
                          />
                          <Label htmlFor="rate-limit" className="text-xs cursor-pointer">
                            Book rate only for amount paid (remaining weight at delivery rate)
                          </Label>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Card>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/orders")}>Cancel</Button>
          <Button type="submit" disabled={submitting} className="bg-zinc-900 hover:bg-zinc-800" data-testid="order-create-submit">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Create Order
          </Button>
        </div>
      </form>
    </div>
  );
}
